import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Download } from "lucide-react";
import { FadeUp } from "./Reveal";
import { PolicyDialog } from "./PolicyDialog";
import { openBrochureModal } from "./BrochureModal";
import { CONTACTS } from "@/data/apartments";
import { API, formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/gtag";
import { useLang } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { nome: "", cognome: "", email: "", telefono: "", tipologia: "", messaggio: "", privacy: false };

const fieldCls =
  "rounded-none border-0 border-b border-ivory/25 bg-transparent px-0 py-3 text-ivory placeholder:text-ivory/35 transition-colors duration-300 focus-visible:ring-0 focus-visible:border-sun";

export const ContactSection = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, lang });
      track("generate_lead", { form: "contatti", tipologia: form.tipologia });
      toast.success(t.contact.success);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const INFO = [
    [MapPin, CONTACTS.indirizzo, "contact-address", null],
    [Phone, CONTACTS.telefono, "contact-phone", `tel:${CONTACTS.telefono.replace(/ /g, "")}`],
    [Mail, CONTACTS.email, "contact-email", null],
    [Clock, t.contact.orari, "contact-hours", null],
  ];

  return (
    <section id="contatti" className="bg-ink py-28 text-ivory sm:py-40" data-testid="contact-section">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-20 px-6 sm:px-10 lg:grid-cols-2 lg:gap-28">
        <div>
          <FadeUp>
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-sun">{t.contact.label}</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-light leading-tight tracking-tight sm:text-6xl">
              {t.contact.h2a}
              <em className="italic text-sun">{t.contact.h2em}</em>
              {t.contact.h2b}
            </h2>
          </FadeUp>
          <FadeUp delay={0.18}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-ivory/70 sm:text-lg">{t.contact.p}</p>
          </FadeUp>
          <div className="mt-14 space-y-6">
            {INFO.map(([Icon, text, testid, href], i) => (
              <FadeUp key={testid} delay={0.05 * i} y={20}>
                <div className="flex items-center gap-4" data-testid={testid}>
                  <Icon className="h-5 w-5 shrink-0 text-sand" strokeWidth={1.25} />
                  {href ? (
                    <a
                      href={href}
                      onClick={() => track("click_telefono")}
                      className="text-sm text-ivory/85 transition-colors hover:text-sun sm:text-base"
                    >
                      {text}
                    </a>
                  ) : (
                    <span className="text-sm text-ivory/85 sm:text-base">{text}</span>
                  )}
                </div>
              </FadeUp>
            ))}
            <FadeUp delay={0.25} y={20}>
              <button
                onClick={openBrochureModal}
                data-testid="contact-brochure-link"
                className="flex items-center gap-4 text-sm text-sun underline underline-offset-4 transition-colors hover:text-ivory sm:text-base"
              >
                <Download className="h-5 w-5 shrink-0" strokeWidth={1.25} />
                {t.brochure}
              </button>
            </FadeUp>
          </div>
        </div>

        <FadeUp delay={0.15}>
          <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              <Input required placeholder={t.contact.nome} value={form.nome} onChange={set("nome")} className={fieldCls} data-testid="form-nome" />
              <Input placeholder={t.contact.cognome} value={form.cognome} onChange={set("cognome")} className={fieldCls} data-testid="form-cognome" />
            </div>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              <Input required type="email" placeholder={t.contact.email} value={form.email} onChange={set("email")} className={fieldCls} data-testid="form-email" />
              <Input required type="tel" placeholder={t.contact.telefono} value={form.telefono} onChange={set("telefono")} className={fieldCls} data-testid="form-telefono" />
            </div>
            <Select value={form.tipologia} onValueChange={(v) => setForm((f) => ({ ...f, tipologia: v }))}>
              <SelectTrigger className={`${fieldCls} h-auto`} data-testid="form-tipologia">
                <SelectValue placeholder={t.contact.tipologia} />
              </SelectTrigger>
              <SelectContent className="border-line bg-ivory text-ink">
                {t.contact.tipologie.map((tp) => (
                  <SelectItem key={tp} value={tp} data-testid={`form-tipologia-${tp.toLowerCase().replace(/ /g, "-")}`}>
                    {tp}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              placeholder={t.contact.messaggio}
              value={form.messaggio}
              onChange={set("messaggio")}
              rows={4}
              className={`${fieldCls} resize-none`}
              data-testid="form-messaggio"
            />
            <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-ivory/60">
              <Checkbox
                checked={form.privacy}
                onCheckedChange={(v) => setForm((f) => ({ ...f, privacy: !!v }))}
                className="mt-0.5 border-ivory/40 data-[state=checked]:border-terracotta data-[state=checked]:bg-terracotta"
                data-testid="form-privacy"
              />
              <span>
                {t.contact.privacy}{" "}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setPrivacyOpen(true);
                  }}
                  className="text-sun underline underline-offset-2 transition-colors hover:text-ivory"
                  data-testid="form-privacy-link"
                >
                  {t.contact.privacyLink}
                </button>
              </span>
            </label>
            <button
              type="submit"
              disabled={sending || !form.privacy}
              data-testid="form-submit"
              className="rounded-full bg-terracotta px-12 py-5 text-[11px] font-semibold uppercase tracking-[0.24em] text-ivory transition-colors duration-500 hover:bg-sun hover:text-ink disabled:cursor-not-allowed disabled:bg-ivory/10 disabled:text-ivory/60"
            >
              {sending ? t.contact.sending : t.contact.submit}
            </button>
          </form>
        </FadeUp>
      </div>
      <PolicyDialog open={privacyOpen} onOpenChange={setPrivacyOpen} type="privacy" />
    </section>
  );
};
